import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, ShieldOff, RotateCcw, History } from 'lucide-react';
import { accountService } from '../api/accountRepo';
import { Account, AccountEvent } from '../types';
import { getRoleDisplayName } from '../utils';
import { canManageStaffDB } from '../../../lib/rbac';
import { useStore } from '../../../lib/store';
import toast from 'react-hot-toast';

const AccountView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useStore();
  const [account, setAccount] = useState<Account | null>(null);
  const [events, setEvents] = useState<AccountEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    checkPermissions();
  }, [user]);

  useEffect(() => {
    if (id && !checkingPermissions && canManageAccounts) {
      loadAccount();
      loadEvents();
    }
  }, [id, checkingPermissions, canManageAccounts]);

  const checkPermissions = async () => {
    if (!user?.id) {
      setCanManageAccounts(false);
      setCheckingPermissions(false);
      return;
    }

    try {
      const canManage = await canManageStaffDB(user.id);
      setCanManageAccounts(canManage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAccounts(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  const loadAccount = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await accountService.get(id);
      if (!data) {
        toast.error('Account not found');
        navigate('/staff/accounts');
        return;
      }
      setAccount(data);
    } catch (error) {
      toast.error('Failed to load account');
      console.error('Error loading account:', error);
      navigate('/staff/accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!id) return;
    
    try {
      const data = await accountService.getEvents(id);
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'disabled') => {
    if (!account) return;
    
    try {
      await accountService.setStatus(account.id, newStatus);
      toast.success(`Account ${newStatus}`);
      loadAccount();
      loadEvents();
    } catch (error) {
      toast.error(`Failed to ${newStatus} account`);
      console.error('Error updating status:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!account) return;
    
    try {
      const result = await accountService.resetPassword(account.id);
      toast.success(`Password reset. Temp password: ${result.tempPassword}`);
      loadEvents();
    } catch (error) {
      toast.error('Failed to reset password');
      console.error('Error resetting password:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      invited: "bg-yellow-100 text-yellow-800",
      disabled: "bg-red-100 text-red-800"
    };
    return colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  if (checkingPermissions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canManageAccounts) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Account not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/staff/accounts')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{account.email}</h1>
            <p className="text-gray-600">{getRoleDisplayName(account.role)} Account</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {account.status === 'active' ? (
            <button
              onClick={() => handleStatusChange('disabled')}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
            >
              <ShieldOff className="w-4 h-4" />
              <span>Disable</span>
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange('active')}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <Shield className="w-4 h-4" />
              <span>Activate</span>
            </button>
          )}
          <button
            onClick={handleResetPassword}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset Password</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Email</div>
                <div className="text-sm font-medium text-gray-900">{account.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Phone</div>
                <div className="text-sm font-medium text-gray-900">{account.phone || 'Not provided'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Role</div>
                <div className="text-sm font-medium text-gray-900">{getRoleDisplayName(account.role)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.status)}`}>
                  {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                </span>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Login</div>
                <div className="text-sm font-medium text-gray-900">
                  {account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleString() : 'Never'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(account.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <History className="w-5 h-5 mr-2" />
              Audit Trail
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map((event) => (
                <div key={event.id} className="border-l-2 border-gray-200 pl-3">
                  <div className="text-sm font-medium text-gray-900 capitalize">
                    {event.action.replace('_', ' ')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(event.createdAt).toLocaleString()}
                  </div>
                  {event.details && (
                    <div className="text-xs text-gray-600 mt-1">{event.details}</div>
                  )}
                </div>
              ))}
              {events.length === 0 && (
                <div className="text-sm text-gray-500">No events recorded</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountView;
